import { supabase } from '../../../shared/lib/supabase';

export interface ProjectInvitation {
  id: string;
  project_id: string;
  owner_id: string;
  invited_email: string;
  invited_user_id?: string;
  permission_level: 'view' | 'edit';
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  invitation_token: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  accepted_at?: string;
  declined_at?: string;
  // From joined data
  project_name?: string;
  project_description?: string;
  owner_email?: string;
  invited_user_email?: string;
}

export interface ProjectShare {
  id: string;
  project_id: string;
  owner_id: string;
  shared_with_id: string;
  permission_level: 'view' | 'edit';
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_name?: string;
}

/**
 * Send invitation email using Supabase's built-in email service
 */
async function sendInvitationEmail(invitation: ProjectInvitation): Promise<void> {
  const invitationUrl = `${window.location.origin}/invitations/${invitation.invitation_token}`;
  
  try {
    console.log('📧 Sending invitation email via Supabase built-in service...');
    
    // Get project details
    const { data: project } = await supabase
      .from('projects')
      .select('name, description')
      .eq('id', invitation.project_id)
      .single();
    
    // Use Supabase's built-in email via magic link
    // This will send an actual email using Supabase's infrastructure
    const { data, error } = await supabase.auth.signInWithOtp({
      email: invitation.invited_email,
      options: {
        shouldCreateUser: false, // Don't create a user account
        emailRedirectTo: invitationUrl,
        data: {
          type: 'project_invitation',
          project_name: project?.name || 'Project',
          project_id: invitation.project_id,
          permission_level: invitation.permission_level,
          invitation_token: invitation.invitation_token,
          message: `You've been invited to collaborate on "${project?.name}" with ${invitation.permission_level} access.`
        }
      }
    });

    if (error) {
      console.error('❌ Supabase email error:', error);
      
      // If the magic link fails, try a different approach
      console.log('📧 Trying alternative email method...');
      
      // Alternative: Send a password reset email with custom redirect
      const { data: resetData, error: resetError } = await supabase.auth.resetPasswordForEmail(
        invitation.invited_email,
        {
          redirectTo: invitationUrl
        }
      );

      if (resetError) {
        console.error('❌ Password reset email also failed:', resetError);
        throw resetError;
      }

      console.log('✅ Alternative email method succeeded');
      return;
    }

    console.log('✅ Invitation email sent successfully via Supabase OTP');
    
  } catch (error) {
    console.error('❌ All email methods failed:', error);
    
    // Final fallback - just log the details for manual sharing
    console.log('📧 Email sending failed. Manual invitation details:');
    console.log('🔗 Invitation link:', invitationUrl);
    console.log('📧 Send to:', invitation.invited_email);
    console.log('⏰ Expires:', new Date(invitation.expires_at).toLocaleDateString());
    console.warn('⚠️  Invitation was created but email sending failed. User can still access via invitation link.');
  }
}

/**
 * Send a project invitation to a user by email
 */
export async function inviteUserToProject(
  projectId: string, 
  email: string, 
  permission: 'view' | 'edit'
): Promise<ProjectInvitation> {
  // Get current user
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('User not authenticated');
  }

  // Check if user is trying to invite themselves
  if (user.email === email) {
    throw new Error('Cannot invite yourself to a project');
  }

  try {
    // Find user by email
    const { data: targetUser, error: findError } = await supabase
      .rpc('get_user_by_email', { email_address: email });

    if (findError) {
      console.error('Find user error:', findError);
      throw new Error('Failed to find user');
    }

    if (!targetUser || targetUser.length === 0) {
      throw new Error('User not found');
    }

    const targetUserId = targetUser[0].id;

    // Check if user is already shared with
    const { data: existingShare } = await supabase
      .from('project_shares')
      .select('id')
      .eq('project_id', projectId)
      .eq('shared_with_id', targetUserId)
      .single();

    if (existingShare) {
      throw new Error('User already has access to this project');
    }

    // Check if user already has pending invitation - if so, update it
    const { data: existingInvitation } = await supabase
      .from('project_invitations')
      .select('id, status')
      .eq('project_id', projectId)
      .eq('invited_email', email)
      .eq('status', 'pending')
      .single();

    if (existingInvitation) {
      // Update the existing invitation instead of creating a new one
      const { data, error } = await supabase
        .from('project_invitations')
        .update({
          permission_level: permission,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingInvitation.id)
        .select('*')
        .single();

      if (error) {
        throw new Error('Failed to update existing invitation');
      }

      // Send email notification
      await sendInvitationEmail(data);
      return data;
    }

    // Create new invitation
    const { data, error } = await supabase
      .from('project_invitations')
      .insert({
        project_id: projectId,
        owner_id: user.id,
        invited_email: email,
        invited_user_id: targetUserId,
        permission_level: permission
      })
      .select('*')
      .single();

    if (error) {
      console.error('Create invitation error:', error);
      throw new Error('Failed to create invitation');
    }

    // Send email notification
    await sendInvitationEmail(data);

    return data;
  } catch (error: any) {
    console.error('Invitation error:', error);
    throw error;
  }
}

/**
 * Get all invitations for a project (for project owners)
 */
export async function getProjectInvitations(projectId: string): Promise<ProjectInvitation[]> {
  try {
    const { data, error } = await supabase
      .from('project_invitations_with_details')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Get invitations error:', error);
      
      // If view doesn't exist, try basic table
      if (error.code === '42P01') {
        const { data: basicData, error: basicError } = await supabase
          .from('project_invitations')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });
        
        if (basicError) {
          throw new Error('Project invitations table not found');
        }
        
        return basicData || [];
      }
      
      throw new Error('Failed to fetch project invitations');
    }

    return data || [];
  } catch (error: any) {
    console.error('Get invitations error:', error);
    throw error;
  }
}

/**
 * Get invitations received by the current user
 */
export async function getMyInvitations(): Promise<ProjectInvitation[]> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('User not authenticated');
  }

  try {
    const { data, error } = await supabase
      .from('project_invitations_with_details')
      .select('*')
      .eq('invited_email', user.email)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Get my invitations error:', error);
      return [];
    }

    return data || [];
  } catch (error: any) {
    console.error('Get my invitations error:', error);
    return [];
  }
}

/**
 * Get invitation by token (for email links)
 */
export async function getInvitationByToken(token: string): Promise<ProjectInvitation | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_invitation_by_token', { token });

    if (error) {
      console.error('Get invitation by token error:', error);
      throw new Error('Failed to fetch invitation');
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (error: any) {
    console.error('Get invitation by token error:', error);
    throw error;
  }
}

/**
 * Accept a project invitation
 */
export async function acceptInvitation(invitationId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('accept_project_invitation', { invitation_id: invitationId });

    if (error) {
      console.error('Accept invitation error:', error);
      throw new Error('Failed to accept invitation');
    }

    return data === true;
  } catch (error: any) {
    console.error('Accept invitation error:', error);
    throw error;
  }
}

/**
 * Decline a project invitation
 */
export async function declineInvitation(invitationId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('decline_project_invitation', { invitation_id: invitationId });

    if (error) {
      console.error('Decline invitation error:', error);
      throw new Error('Failed to decline invitation');
    }

    return data === true;
  } catch (error: any) {
    console.error('Decline invitation error:', error);
    throw error;
  }
}

/**
 * Cancel/delete a project invitation (for project owners)
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('project_invitations')
      .delete()
      .eq('id', invitationId);

    if (error) {
      console.error('Cancel invitation error:', error);
      throw new Error('Failed to cancel invitation');
    }
  } catch (error: any) {
    console.error('Cancel invitation error:', error);
    throw error;
  }
}

/**
 * Get all shares for a project
 */
export async function getProjectShares(projectId: string): Promise<ProjectShare[]> {
  try {
    // First get the project shares
    const { data: shares, error: sharesError } = await supabase
      .from('project_shares')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (sharesError) {
      console.error('Get shares error:', sharesError);
      return [];
    }

    if (!shares || shares.length === 0) {
      return [];
    }

    // Get user details for each share
    const userDetailsPromises = shares.map(async (share) => {
      try {
        const { data: users, error } = await supabase
          .rpc('get_user_by_id', { user_id: share.shared_with_id });
        
        if (error || !users || users.length === 0) {
          return {
            ...share,
            user_email: 'Unknown',
            user_name: 'Unknown'
          };
        }
        
        const user = users[0];
        return {
          ...share,
          user_email: user.email || 'Unknown',
          user_name: user.raw_user_meta_data?.full_name || user.email || 'Unknown'
        };
      } catch (error) {
        console.error('Error getting user details:', error);
        return {
          ...share,
          user_email: 'Unknown',
          user_name: 'Unknown'
        };
      }
    });

    const sharesWithUserDetails = await Promise.all(userDetailsPromises);
    return sharesWithUserDetails;
  } catch (error: any) {
    console.error('Get project shares error:', error);
    return [];
  }
}

/**
 * Update permission level for a project share
 */
export async function updateProjectShare(
  shareId: string, 
  permission: 'view' | 'edit'
): Promise<void> {
  try {
    const { error } = await supabase
      .from('project_shares')
      .update({ permission_level: permission })
      .eq('id', shareId);

    if (error) {
      console.error('Update share error:', error);
      throw new Error('Failed to update share permission');
    }
  } catch (error: any) {
    console.error('Update share error:', error);
    throw error;
  }
}

/**
 * Remove a project share
 */
export async function removeProjectShare(shareId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('project_shares')
      .delete()
      .eq('id', shareId);

    if (error) {
      console.error('Remove share error:', error);
      throw new Error('Failed to remove project share');
    }
  } catch (error: any) {
    console.error('Remove share error:', error);
    throw error;
  }
}

/**
 * Check if user has permission to access a project
 */
export async function checkProjectPermission(
  projectId: string
): Promise<{ hasAccess: boolean; permission: 'owner' | 'view' | 'edit' | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    return { hasAccess: false, permission: null };
  }

  try {
    // Check if user owns the project
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('user_id')
      .eq('id', projectId)
      .single();

    if (projectError) {
      return { hasAccess: false, permission: null };
    }

    if (project.user_id === user.id) {
      return { hasAccess: true, permission: 'owner' };
    }

    // Check if project is shared with user
    const { data: share, error: shareError } = await supabase
      .from('project_shares')
      .select('permission_level')
      .eq('project_id', projectId)
      .eq('shared_with_id', user.id)
      .single();

    if (shareError || !share) {
      return { hasAccess: false, permission: null };
    }

    return { hasAccess: true, permission: share.permission_level };
  } catch (error: any) {
    console.error('Check permission error:', error);
    return { hasAccess: false, permission: null };
  }
}